/**
 * useSiteMeta — fetches /api/site-meta once on mount and applies the values
 * to `document.head` (title, meta description, favicon, theme color,
 * Open Graph + Twitter tags, robots, canonical).
 *
 * The values come from the singleton `site_meta` Mongo document which the
 * admin edits at /admin/site-meta. This replaces the hardcoded
 * "Emergent | Fullstack App" title and "A product of emergent.sh" meta
 * description that ship in public/index.html.
 *
 * No-op if the fetch fails — the static `index.html` defaults stay in place.
 */
import { useEffect } from "react";
import api from "@/lib/api";

function backendOrigin() {
  return (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
}

function resolveAssetUrl(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/api/")) return `${backendOrigin()}${url}`;
  return url;
}

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function apply(meta) {
  if (meta.page_title) document.title = meta.page_title;
  setMeta("description", meta.meta_description);
  setMeta("theme-color", meta.theme_color);
  setMeta("keywords", meta.keywords);
  setMeta("robots", meta.robots);

  // Open Graph (Facebook / LinkedIn / iMessage previews)
  setMeta("og:title", meta.og_title || meta.page_title, "property");
  setMeta(
    "og:description",
    meta.og_description || meta.meta_description,
    "property",
  );
  const ogImg = resolveAssetUrl(meta.og_image_url);
  if (ogImg) setMeta("og:image", ogImg, "property");
  setMeta("og:type", "website", "property");

  // Twitter card
  setMeta("twitter:card", meta.twitter_card || "summary_large_image");
  setMeta("twitter:title", meta.og_title || meta.page_title);
  setMeta(
    "twitter:description",
    meta.og_description || meta.meta_description,
  );
  if (ogImg) setMeta("twitter:image", ogImg);

  // Favicon — replace whatever <link rel="icon"> shipped in index.html
  const fav = resolveAssetUrl(meta.favicon_url);
  if (fav) {
    setLink("icon", fav);
    setLink("shortcut icon", fav);
    setLink("apple-touch-icon", fav);
  }

  // Canonical
  if (meta.canonical_url) setLink("canonical", meta.canonical_url);
}

export default function useSiteMeta() {
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/site-meta");
        if (!cancelled && data) apply(data);
      } catch {
        // Silent — keep static defaults from public/index.html.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
}
